import * as React from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { DataTable, ColumnHelper } from "@/components/data-table"
import { SmartForm } from "@/components/smart-form"
import { StatsCard } from "@/components/stats-card"
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalFooter } from "@/components/modal"
import { Drawer, DrawerHeader, DrawerTitle, DrawerContent } from "@/components/drawer"
import { ConfirmDialog } from "@/components/confirm-dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { useNotificationStore } from "@/store"
import { api } from "@/lib/api"
import { Plus, Pencil, Trash2, Eye, RefreshCw } from "lucide-react"

function ResourcePage({
  resource,
  title,
  columns: columnDefs,
  formFields,
  formSchema,
  statsEndpoint,
  customActions,
  renderRow,
  onRowClick,
}) {
  const queryClient = useQueryClient()
  const addNotification = useNotificationStore((s) => s.addNotification)

  const [state, setState] = React.useState({
    isFormOpen: false,
    isViewOpen: false,
    isDeleteOpen: false,
    selectedItem: null,
    viewItem: null,
  })

  // Fetch data
  const { data, isLoading, refetch } = useQuery({
    queryKey: [resource],
    queryFn: () => api.get(`/${resource}`),
  })

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: [`${resource}-stats`],
    queryFn: () => api.get(statsEndpoint || `/${resource}/stats`),
    enabled: !!statsEndpoint,
  })

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => api.post(`/${resource}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries([resource])
      addNotification({ type: "success", message: `${title} created successfully` })
      setState((s) => ({ ...s, isFormOpen: false, selectedItem: null }))
    },
    onError: (error) => {
      addNotification({ type: "error", message: error.message })
    },
  })

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data) => api.put(`/${resource}/${state.selectedItem.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries([resource])
      addNotification({ type: "success", message: `${title} updated successfully` })
      setState((s) => ({ ...s, isFormOpen: false, selectedItem: null }))
    },
    onError: (error) => {
      addNotification({ type: "error", message: error.message })
    },
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/${resource}/${state.selectedItem.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries([resource])
      addNotification({ type: "success", message: `${title} deleted successfully` })
      setState((s) => ({ ...s, isDeleteOpen: false, selectedItem: null }))
    },
    onError: (error) => {
      addNotification({ type: "error", message: error.message })
    },
  })

  // Table columns
  const colHelper = ColumnHelper()
  const columns = React.useMemo(() => {
    const cols = columnDefs.map((col) => {
      if (col.type === "status") return colHelper.status(col.key, col.options)
      if (col.type === "boolean") return colHelper.boolean(col.key, col.options)
      if (col.type === "date") return colHelper.date(col.key, col.options)
      if (col.type === "number") return colHelper.number(col.key, col.options)
      return colHelper.text(col.key, col.options)
    })

    cols.push(
      colHelper.actions([
        {
          icon: <Eye className="h-4 w-4" />,
          onClick: (row) => setState((s) => ({ ...s, isViewOpen: true, viewItem: row })),
        },
        {
          icon: <Pencil className="h-4 w-4" />,
          onClick: (row) => setState((s) => ({ ...s, isFormOpen: true, selectedItem: row })),
        },
        {
          icon: <Trash2 className="h-4 w-4" />,
          variant: "ghost",
          onClick: (row) => setState((s) => ({ ...s, isDeleteOpen: true, selectedItem: row })),
        },
      ])
    )

    return cols
  }, [columnDefs])

  const handleSubmit = (data) => {
    if (state.selectedItem) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
          <p className="text-muted-foreground">Manage your {title.toLowerCase()}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={() => setState((s) => ({ ...s, isFormOpen: true, selectedItem: null }))}>
            <Plus className="mr-2 h-4 w-4" />
            Add {title}
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats?.data && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Object.entries(stats.data).map(([key, value]) => (
            <StatsCard key={key} title={key} value={value} />
          ))}
        </div>
      )}

      {/* Table */}
      <DataTable
        columns={columns}
        data={data?.data || []}
        searchKey="name"
        searchPlaceholder={`Search ${title.toLowerCase()}...`}
        onRefresh={refetch}
        isLoading={isLoading}
      />

      {/* Form Drawer */}
      <Drawer open={state.isFormOpen} onClose={() => setState((s) => ({ ...s, isFormOpen: false, selectedItem: null }))}>
        <DrawerHeader>
          <DrawerTitle>
            {state.selectedItem ? `Edit ${title}` : `Create ${title}`}
          </DrawerTitle>
        </DrawerHeader>
        <SmartForm
          schema={formSchema}
          fields={formFields}
          defaultValues={state.selectedItem || {}}
          onSubmit={handleSubmit}
          onCancel={() => setState((s) => ({ ...s, isFormOpen: false, selectedItem: null }))}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      </Drawer>

      {/* View Modal */}
      <Modal open={state.isViewOpen} onOpenChange={(open) => setState((s) => ({ ...s, isViewOpen: open }))}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>View {title}</ModalTitle>
          </ModalHeader>
          <div className="space-y-4">
            {state.viewItem && Object.entries(state.viewItem).map(([key, value]) => (
              <div key={key} className="flex justify-between">
                <span className="text-muted-foreground">{key}</span>
                <span className="font-medium">{String(value)}</span>
              </div>
            ))}
          </div>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={state.isDeleteOpen}
        onClose={() => setState((s) => ({ ...s, isDeleteOpen: false, selectedItem: null }))}
        onConfirm={() => deleteMutation.mutate()}
        title={`Delete ${title}`}
        description={`Are you sure you want to delete this ${title.toLowerCase()}? This action cannot be undone.`}
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}

export { ResourcePage }
